import { createApi } from '@reduxjs/toolkit/query/react';
import baseQueryWithReauth from '@/pages/shared/base-query-auth';
import {
    isUnauthorizedError,
    runMutationOnce,
    runQueryOnce,
    stripEmptyParams,
} from '@/pages/shared/rtk-query-helpers';

const toDashboardFiltersPayload = (model) => {
    const toTokens = (value) => {
        if (!value) {
            return [];
        }

        return Array.isArray(value) ? value : value.split(' ');
    };

    return {
        ...model,
        documentNumber: toTokens(model && model.documentNumber),
        keywords: toTokens(model && model.keywords),
        vendorNumber: toTokens(model && model.vendorNumber),
    };
};

export const documentFiltersApi = createApi({
    reducerPath: 'documentFiltersApi',
    baseQuery: baseQueryWithReauth,
    endpoints: (builder) => ({
        // The queries below keep `keepUnusedDataFor: 0` so cached filter lists cannot go
        // stale behind category/status changes. Callers pass `forceRefetch`, and RTK Query
        // still collapses concurrent identical requests, replacing the hand-rolled
        // pending-request map the old axios service used.
        getCategoryList: builder.query({
            query: ({ siteId, subCategory, documentStatusId, filterOptions } = {}) => ({
                url: '/api/document/categoriesList',
                method: 'GET',
                params: stripEmptyParams({
                    siteId,
                    subCategory,
                    documentStatusId: documentStatusId || 0,
                    ...filterOptions,
                }),
            }),
            keepUnusedDataFor: 0,
        }),
        getSubCategoryList: builder.query({
            query: ({ siteId, category, documentStatusId, filterOptions } = {}) => ({
                url: '/api/document/subCategoriesList',
                method: 'GET',
                params: stripEmptyParams({
                    siteId,
                    category,
                    documentStatusId: documentStatusId || 0,
                    ...filterOptions,
                }),
            }),
            keepUnusedDataFor: 0,
        }),
        getDocumentFilters: builder.query({
            query: (siteId) => ({
                url: '/api/document/documentFilters',
                method: 'GET',
                params: stripEmptyParams({ siteId }),
            }),
            keepUnusedDataFor: 0,
        }),
        getCheckers: builder.query({
            query: (siteId) => ({
                url: '/api/user/getCheckers',
                method: 'GET',
                params: stripEmptyParams({ siteId }),
            }),
            keepUnusedDataFor: 0,
        }),
        // Modelled as a mutation because it is a POST search whose results must never be
        // served from cache.
        getDashboardFilters: builder.mutation({
            query: (model) => ({
                url: '/api/document/dashboardFilters',
                method: 'POST',
                body: toDashboardFiltersPayload(model),
            }),
        }),
    }),
});

export const {
    useGetCategoryListQuery,
    useGetSubCategoryListQuery,
    useGetDocumentFiltersQuery,
    useGetCheckersQuery,
    useGetDashboardFiltersMutation,
} = documentFiltersApi;

/**
 * Runs a query endpoint once and releases its cache subscription, so thunk-style
 * callers behave like the imperative service calls they replace.
 */
export const runDocumentFiltersQuery = (dispatch, endpointName, arg) =>
    runQueryOnce(dispatch, documentFiltersApi.endpoints[endpointName], arg);

/**
 * Runs a mutation endpoint once and clears its tracked entry.
 */
export const runDocumentFiltersMutation = (dispatch, endpointName, arg) =>
    runMutationOnce(dispatch, documentFiltersApi.endpoints[endpointName], arg);

export { isUnauthorizedError };

export default documentFiltersApi;
